import EmberService, { service } from '@ember/service';

import { pluralize } from 'ember-inflector';

import type { RecordData, Service } from 'webapp-types';
import { ModelName, ReportSlideFragmentType, TemplateObjectType } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';

import type Block from 'webapp/models/block';
import type BlockPreview from 'webapp/models/block-preview';
import type ReportSlideRecord from 'webapp/models/report-slide';
import type ReportSlideFragmentRecord from 'webapp/models/report-slide-fragment';

// a record in a socket payload
interface PayloadRecord {
	id: string | number;
}

// the fields of a record, model or plain object that clearConnectedRecords finds the connected records by
interface ConnectedRecordsData {
	id?: string | number | null;
	previewHash?: string;
	blockId?: number | null;
	objectType?: TemplateObjectType;
}

// the unload helpers coerce ids themselves and skip a missing one
type RecordId = string | number | null | undefined;

export default class GarbageCollectorService extends EmberService {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/client-metrics')
	declare mmClientMetrics: Service.ModelManager.ClientMetrics;

	filterSocketDataUpdates(payload: RecordData.Payload): RecordData.Payload {
		// only leave blocks that are on boards where data has been requested
		// or blocks already in the store (e.g. report fragment blocks with boardId: null)
		// NOTE: add here any other exceptions for blocks that arrive over a pusher event
		// for the first time (i.e. not yet in the store) — e.g. blocks owned by a goal,
		// which have boardId: null and aren't in savedBlockIds yet on goal creation.
		const savedBlockIds = new Set(this.mmBlocksService.savedRecords.map((i) => String(i.id)));
		const blockFilterFn = (item: PayloadRecord & { boardId?: number | null }) => {
			return (
				this.mmBoardsService.isBoardDataRequested(String(item.boardId)) ||
				this.mmBoardsService.isBoardDataRequesting(String(item.boardId)) ||
				savedBlockIds.has(String(item.id)) ||
				this.mmGoalsService.hasGoalForBlockId(item.id)
			);
		};

		if (payload.blocks) {
			payload.blocks = payload.blocks.filter(blockFilterFn);
		} else if (payload.block && !blockFilterFn(payload.block)) {
			delete payload.block;
		}

		Object.keys(payload).forEach((key) => {
			const pluralKey = pluralize(key),
				isArray = key === pluralKey;

			let filterFn;

			if (pluralKey === 'samples') {
				// only let through samples that are either on requested blocks / block previews
				// or belong to goals or metrics (that are requested!)
				let samplesNeeded: string[] = [];
				if (payload.blocks) {
					samplesNeeded.push(...payload.blocks.map((i) => String(i.id)));
				}
				if (payload.block) {
					samplesNeeded.push(String(payload.block.id));
				}
				samplesNeeded.push(...this.mmBlocksService.savedRecords.map((i) => i.id!));

				if (payload.blockPreviews) {
					samplesNeeded.push(...payload.blockPreviews.map((i) => String(i.id)));
				}
				if (payload.blockPreview) {
					samplesNeeded.push(String(payload.blockPreview.id));
				}
				samplesNeeded.push(...this.mmBlockPreviewsService.savedRecords.map((i) => i.id!));

				if (payload.goals) {
					samplesNeeded.push(...payload.goals.map((i) => String(i.blockId)));
				}
				if (payload.goal) {
					samplesNeeded.push(String(payload.goal.blockId));
				}
				samplesNeeded.push(
					...this.mmGoalsService.savedRecords.map((i) => String(i.blockId)),
				);

				if (payload.clientMetrics) {
					samplesNeeded.push(...payload.clientMetrics.map((i) => String(i.blockId)));
				}

				if (payload.clientMetric) {
					samplesNeeded.push(String(payload.clientMetric.blockId));
				}

				samplesNeeded.push(
					...this.mmClientMetrics.savedRecords.map((i) => String(i.blockId)),
				);

				if (payload.forecasts) {
					samplesNeeded.push(...payload.forecasts.map((i) => String(i.blockId)));
				}
				if (payload.forecast) {
					samplesNeeded.push(String(payload.forecast.blockId));
				}
				samplesNeeded.push(
					...this.mmForecastsService.savedRecords.map((i) => String(i.blockId)),
				);

				samplesNeeded = uniq(samplesNeeded);
				filterFn = (item: PayloadRecord) => {
					// only those for which board data is requested
					return samplesNeeded.includes(String(item.id));
				};
			}

			if (filterFn) {
				const records: Record<string, unknown> = payload, // indexed by its own keys
					value = records[key];
				if (isArray) {
					records[key] = (value as PayloadRecord[]).filter(filterFn);
				} else if (!filterFn(value as PayloadRecord)) {
					delete records[key];
				}
			}
		});

		return payload;
	}

	_unloadBoardData(boardId: RecordId, previewHash?: string): void {
		if (!boardId) {
			return;
		}

		// unloading blocks
		this.mmBlocksService.savedRecords
			.filter((item) => item.boardId === +boardId)
			.forEach((block: Block) => {
				this._unloadBlockData(block.id);
				block.unloadRecord();
			});

		if (previewHash) {
			// unloading preview blocks
			this.mmBlockPreviewsService.savedRecords
				.filter((item) => item.previewHash === previewHash)
				.forEach((blockPreview: BlockPreview) => {
					this._unloadBlockData(blockPreview.id);
					blockPreview.unloadRecord();
				});
		}
	}

	_unloadBlockData(blockId: RecordId): void {
		if (!blockId) {
			return;
		}

		// samples
		const sample = this.mmSamplesService.findRecord(String(blockId));
		if (sample) {
			sample.unloadRecord();
		}

		// data-selects
		try {
			this.mmDataSelectsService.getRecordsForBlock(+blockId).forEach((ds) => {
				ds.unloadRecord();
			});
		} catch (_e) {
			// skip - this can throw "Uncaught TypeError: Cannot read property 'id' of null"
			// happens when we are unloading records in the same runloop (https://github.com/emberjs/data/issues/5395)
		}
	}

	private _unloadReportData(reportId: RecordId) {
		if (!reportId) {
			return;
		}

		// unloading report slides
		this.mmReportSlidesService.savedRecords
			.filter((item) => item.reportId === +reportId)
			.forEach((reportSlide: ReportSlideRecord) => {
				this._unloadReportSlideData(reportSlide.id);
				reportSlide.unloadRecord();
			});
	}

	private _unloadReportSlideData(reportSlideId: RecordId): void {
		if (!reportSlideId) {
			return;
		}

		// unloading report slide fragments
		this.mmReportSlideFragmentsService.savedRecords
			.filter((item) => item.reportSlideId === +reportSlideId)
			.forEach((reportSlideFragment: ReportSlideFragmentRecord) => {
				if (reportSlideFragment.type === ReportSlideFragmentType.BOARD) {
					this.mmBoardsService.unloadRequestedBoard(
						String(reportSlideFragment.referenceId),
					);
				} else if (
					[ReportSlideFragmentType.BLOCK, ReportSlideFragmentType.STORY_TEXT].includes(
						reportSlideFragment.type,
					)
				) {
					this._unloadBlockData(String(reportSlideFragment.referenceId));
				}
				reportSlideFragment.unloadRecord();
			});
	}

	private _unloadTemplateData(templateId: RecordId, objectType: TemplateObjectType | undefined) {
		if (!templateId) {
			return;
		}

		if (objectType === TemplateObjectType.BOARD) {
			const board = this.mmBoardsService.savedRecords.find(
				(item) => item.templateId === +templateId,
			);

			if (board) {
				this._unloadBoardData(board.id);
				board.unloadRecord();
			}
		}

		if (objectType === TemplateObjectType.REPORT) {
			const report = this.mmReportsService.savedRecords.find(
				(item) => item.templateId === +templateId,
			);

			if (report) {
				this._unloadReportData(report.id);
				report.unloadRecord();
			}
		}
	}

	clearConnectedRecords(type: string, modelData: ConnectedRecordsData): void {
		switch (type) {
			case ModelName.BOARD: {
				this._unloadBoardData(modelData.id, modelData.previewHash);
				break;
			}
			case ModelName.BLOCK:
			case ModelName.BLOCK_PREVIEW: {
				this._unloadBlockData(modelData.id);
				break;
			}
			case ModelName.GOAL:
			case ModelName.SCORECARD:
			case ModelName.FORECAST: {
				this._unloadBlockData(modelData.blockId);
				break;
			}
			case ModelName.REPORT:
				this._unloadReportData(modelData.id);
				break;
			case ModelName.REPORT_SLIDE:
				this._unloadReportSlideData(modelData.id);
				break;
			case ModelName.TEMPLATE:
				this._unloadTemplateData(modelData.id, modelData.objectType);
				break;
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'garbage-collector': GarbageCollectorService;
	}
}
